// CRM 2.0 block in the header bell drawer (M1-F8, design §8.2). Sits below today's
// "My Task List" block, which stays as it is. Own state and fetches, no full-screen loader:
// the unread count and the latest 10 rows of the last 30 days are read when the drawer opens.
// Shown to users who can open CRM 2.0, or to anyone who has CRM 2.0 rows (e.g. the VJ Signatory).
// A row click marks it read; then View CRM Details opens for its booking (users who can open
// CRM 2.0), else the row opens in place with its full text. The drawer closes on any click
// (MainPage), so an in-place row stops the click; View CRM Details is rendered by MainPage.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCrm2Notifications, markCrm2NotificationRead } from "../../../Services/Crm2Notifications";
import { canOpenCrm2 } from "../ui/access";
import { CRM2_NOTIFICATIONS_PATH, LOAD_ERROR_TEXT, NO_ROWS_TEXT, fullTime, opensBooking, timeAgo } from "./notificationParts";
import css from "./Crm2BellBlock.module.css";

const BELL_ROWS = 10;

// unread = useCrm2UnreadCount() of MainPage (the badge): { count, setCount, refresh }
function Crm2BellBlock({ open, user, unread, onOpenBooking }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [openId, setOpenId] = useState(null);
  const { count, setCount, refresh } = unread;

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    refresh();
    setLoading(true);
    setFailed(false);
    getCrm2Notifications({ days: 30, unread: false, limit: BELL_ROWS })
      .then((d) => {
        if (alive) setRows((d?.rows || []).slice(0, BELL_ROWS));
      })
      .catch((err) => {
        console.warn("[crm2][bell] list failed", { status: err?.response?.status, error: err?.message });
        if (alive) setFailed(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, refresh]);

  const onRow = (e, row) => {
    const opens = opensBooking(row, user);
    if (!opens) e.stopPropagation(); // keep the drawer open: the row shows its full text here
    if (!row.readAt) {
      const at = Date.now();
      setRows((rs) => rs.map((r) => (r.crm2MessageId === row.crm2MessageId ? { ...r, readAt: at } : r)));
      setCount((c) => Math.max(0, c - 1));
      markCrm2NotificationRead(row.crm2MessageId).catch((err) => {
        console.warn("[crm2][bell] mark read failed", { crm2MessageId: row.crm2MessageId, status: err?.response?.status, error: err?.message });
        refresh();
      });
    }
    if (opens) onOpenBooking(row.allotmentPaymentId);
    else setOpenId((id) => (id === row.crm2MessageId ? null : row.crm2MessageId));
  };

  // Enter / Space on a row must not reach the drawer's "close on any key"
  const onRowKey = (e) => {
    if (e.key === "Enter" || e.key === " ") e.stopPropagation();
  };

  if (!canOpenCrm2(user) && !rows.length) return null;

  return (
    <section className={css.block} aria-label="CRM 2.0 notifications">
      <div className={css.head}>
        <span className={css.headTitle}>CRM 2.0 · {count} unread</span>
        <Link to={CRM2_NOTIFICATIONS_PATH} className={css.link}>
          See all
        </Link>
      </div>
      {loading && !rows.length ? (
        <p className={css.note}>Loading…</p>
      ) : failed && !rows.length ? (
        <p className={css.note}>{LOAD_ERROR_TEXT}</p>
      ) : !rows.length ? (
        <p className={css.note}>{NO_ROWS_TEXT}</p>
      ) : (
        <ul className={css.list}>
          {rows.map((r) => {
            const expanded = openId === r.crm2MessageId;
            const inPlace = !opensBooking(r, user);
            return (
              <li key={r.crm2MessageId}>
                <button
                  type="button"
                  className={css.row}
                  title={fullTime(r.created_at)}
                  aria-expanded={inPlace ? expanded : undefined}
                  onClick={(e) => onRow(e, r)}
                  onKeyDown={onRowKey}
                >
                  <span className={css.rowTop}>
                    {!r.readAt && <span className={css.dot} aria-hidden="true" />}
                    {!r.readAt && <span className={css.srOnly}>Unread: </span>}
                    <span className={`${css.title} ${r.readAt ? "" : css.unread} ${expanded ? css.titleFull : ""}`}>{r.title || "CRM 2.0 notification"}</span>
                  </span>
                  <span className={css.meta}>{[r.unitLabel, timeAgo(r.created_at)].filter(Boolean).join(" · ")}</span>
                  {expanded && r.body && <span className={css.body}>{r.body}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default Crm2BellBlock;
